// File: src/English/Grade5/Grade5EnglishLesson.js

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Grade5EnglishLesson.css"; // You can copy and adapt Grade4EnglishLesson.css
import { awardToken } from "../../awardtoken.js";
import { getNounsPool } from "../EnglishTool/pool/nounsPool.js";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool.js";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool.js";
import { getAdverbsPool } from "../EnglishTool/pool/adverbsPool.js";
import { getReadingPool } from "../EnglishTool/pool/readingPool.js";
import { shuffleArray } from "../EnglishTool/shuffleArray.js";
import {
  buildNounTypeMC, buildNounClassifyMC, buildFillInBlankNoun, buildIdentifyNounsQ,
  buildVerbMC, buildFillInBlankVerb, buildIdentifyVerbsQ,
  buildAdjectiveMC, buildFillInBlankAdjective, buildIdentifyAdjectivesQ,
  buildAdverbMC, buildFillInBlankAdverb, buildIdentifyAdverbsQ,
  checkRequiredWord, checkSentenceStructure, makeSampleSentence,
} from "../EnglishTool/quizHelpers.js";
import { getSentencePool } from "../EnglishTool/pool/sentencePool.js";
import { chapterPages } from "./Grade5chapterPage.js";
import { StoryModeMultipleChoice, StoryModeFillInBlank, StoryModeIdentify} from "./StoryMode.js"

function getTopicForChapter(ch) {
  switch (ch) {
    case 1: return "nouns";
    case 2: return "verbs";
    case 3: return "adjectives";
    case 4: return "adverbs";
    case 5: return "reading";
    default: return "nouns";
  }
}

export function generateChapterQuiz(topic, grade, n = 8) {
  const sentencePool = getSentencePool(grade);

  if (topic === "nouns") {
    const nounPool = getNounsPool(grade);
    let questions = [
      buildNounTypeMC(nounPool, "collective"),
      buildNounTypeMC(nounPool, "possessive"),
      buildNounClassifyMC(nounPool),
      buildIdentifyNounsQ(sentencePool),
      buildFillInBlankNoun(shuffleArray(nounPool)[0]),
    ].filter(Boolean);
    return shuffleArray(questions).slice(0, n);
  }

  if (topic === "verbs") {
    const verbPool = getVerbsPool(grade);
    let questions = [
      buildVerbMC(verbPool, grade, { focus: "agreement" }),
      buildVerbMC(verbPool, grade, { focus: "progressive" }),
      buildFillInBlankVerb(shuffleArray(verbPool)[0]),
      buildIdentifyVerbsQ(sentencePool),
    ];
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  if (topic === "adjectives") {
    const adjPool = getAdjectivesPool(grade);
    let questions = [
      buildAdjectiveMC(adjPool, grade, { focus: "order" }),
      buildAdjectiveMC(adjPool, grade, { focus: "comparative" }),
      buildFillInBlankAdjective(shuffleArray(adjPool)[0]),
      buildIdentifyAdjectivesQ(sentencePool),
    ];
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  if (topic === "adverbs") {
    const advPool = getAdverbsPool(grade);
    let questions = [
      buildAdverbMC(advPool, grade, { focus: "frequency" }),
      buildAdverbMC(advPool, grade, { focus: "comparison" }),
      buildFillInBlankAdverb(shuffleArray(advPool)[0]),
      buildIdentifyAdverbsQ(sentencePool),
    ];
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  if (topic === "reading") {
    const pool = getReadingPool(grade);
    const shuffled = shuffleArray(pool).slice(0, n);
    return shuffled.map((item) => {
      const qText = `What is the main idea of: "${item.statement}"?`;
      const correctAnswer = item.mainIdea;
      const distractors = [
        "No main idea given",
        "It's about something else",
        "Nothing relevant",
        "Weather forecast",
        "Famous people"
      ];
      const finalDist = shuffleArray(distractors).slice(0, 3);
      return {
        questionType: "MC",
        question: qText,
        correctAnswer,
        options: shuffleArray([correctAnswer, ...finalDist]),
      };
    });
  }

  return [];
}

function buildFullQuestionBank(grade) {
  const bigSet = [];
  const sentencePool = getSentencePool(grade);

  // Nouns
  const nounPool = getNounsPool(grade);
  bigSet.push(
    buildNounTypeMC(nounPool, "collective"),
    buildNounTypeMC(nounPool, "possessive"),
    buildNounClassifyMC(nounPool),
    buildIdentifyNounsQ(sentencePool),
    buildFillInBlankNoun(shuffleArray(nounPool)[0])
  );

  // Verbs
  const verbPool = getVerbsPool(grade);
  bigSet.push(
    buildVerbMC(verbPool, grade, { focus: "agreement" }),
    buildVerbMC(verbPool, grade, { focus: "progressive" }),
    buildFillInBlankVerb(shuffleArray(verbPool)[0]),
    buildIdentifyVerbsQ(sentencePool)
  );

  // Adjectives
  const adjPool = getAdjectivesPool(grade);
  bigSet.push(
    buildAdjectiveMC(adjPool, grade, { focus: "order" }),
    buildAdjectiveMC(adjPool, grade, { focus: "comparative" }),
    buildFillInBlankAdjective(shuffleArray(adjPool)[0]),
    buildIdentifyAdjectivesQ(sentencePool)
  );

  // Adverbs
  const advPool = getAdverbsPool(grade);
  bigSet.push(
    buildAdverbMC(advPool, grade, { focus: "frequency" }),
    buildAdverbMC(advPool, grade, { focus: "comparison" }),
    buildFillInBlankAdverb(shuffleArray(advPool)[0]),
    buildIdentifyAdverbsQ(sentencePool)
  );

  // Reading
  const readingPool = getReadingPool(grade);
  readingPool.forEach(item => {
    const qText = `What is the main idea of: "${item.statement}"?`;
    const correctAnswer = item.mainIdea;
    const distractors = [
      "No main idea given",
      "It's about something else",
      "Nothing relevant",
      "Weather forecast",
      "Famous people"
    ];
    const finalDist = shuffleArray(distractors).slice(0, 3);
    bigSet.push({
      questionType: "MC",
      question: qText,
      correctAnswer,
      options: shuffleArray([correctAnswer, ...finalDist])
    });
  });

  return shuffleArray(bigSet.filter(Boolean));
}

function generateFinalQuiz(grade, count = 45) {
  const allQs = buildFullQuestionBank(grade);
  const n = Math.min(count, allQs.length);
  const shuffled = shuffleArray(allQs);
  return shuffled.slice(0, n);
}

function FinalQuiz({ cQ, fIdx, finalQs, handleFinalAnswer, fScore }) {
  const [input, setInput] = React.useState("");
  React.useEffect(() => setInput(""), [fIdx]);
  if (!cQ) return <div>Loading...</div>;
  return (
    <div>
      <p>Q {fIdx + 1}/{finalQs.length}: {cQ.question}</p>
      {cQ.questionType === "MC" && cQ.options && (
        cQ.options
          .filter(o => !!o && o.trim() !== "")
          .map((o, i) => (
            <button key={i} className="button1" onClick={() => handleFinalAnswer(o)}>
              {o}
            </button>
          ))
      )}
      {(cQ.questionType === "FITB" || cQ.questionType === "IDENTIFY") && (
        <form onSubmit={e => { e.preventDefault(); handleFinalAnswer(input); }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={cQ.questionType === "IDENTIFY" ? "Type answers separated by commas" : "Type your answer"}
            style={{ minWidth: 240 }}
            autoFocus
          />
          <button className="button1" type="submit">Submit</button>
        </form>
      )}
      <p>Score: {fScore}</p>
      <br />
    </div>
  );
}

/** ================== MAIN COMPONENT ================== **/

export default function Grade5EnglishLesson({ onAwardTokens }) {
  const grade = 5;
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);

  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(360);
  const hasAwardedTokens = useRef(false);

  const [lastFeedback, setLastFeedback] = useState(null);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("loggedInUser"));
    if (savedUser) setUser(savedUser);

    const storedChap = localStorage.getItem("g5English_chapter");
    const storedPage = localStorage.getItem("g5English_pageIdx");
    const done = localStorage.getItem("grade5EnglishComplete");
    if (storedChap) setChapter(parseInt(storedChap, 10) || 1);
    if (storedPage) setPageIdx(parseInt(storedPage, 10) || 0);
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("g5English_chapter", String(chapter));
    localStorage.setItem("g5English_pageIdx", String(pageIdx));
  }, [chapter, pageIdx]);

  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade5EnglishComplete", "true");
    }
  }, [chapDone]);

  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);

  function getTotalPages(obj) {
    return Object.values(obj).reduce((sum, arr) => sum + arr.length, 0);
  }
  function getAbsolutePage(obj, ch, pIdx) {
    let pagesBefore = 0;
    for (let c = 1; c < ch; c++) {
      pagesBefore += obj[c].length;
    }
    return pagesBefore + pIdx + 1;
  }
  const totalPages = getTotalPages(chapterPages);
  const absolutePage = getAbsolutePage(chapterPages, chapter, pageIdx);
  const progress = Math.round((absolutePage / totalPages) * 100);

  /** =========== CHAPTER QUIZ =========== */
  function startChapterQuiz() {
    const topic = getTopicForChapter(chapter);
    const generated = generateChapterQuiz(topic, grade, 8);
    setQuiz(generated);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }

  function handleAnswer(opt) {
    const cQ = quiz[qIdx];
    if (opt === cQ.correctAnswer) {
      setQScore(s => s + 1);
      setLastFeedback({ isCorrect: true, message: "Correct!", correctAnswer: cQ.correctAnswer });
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 600);
    } else {
      setLastFeedback({
        isCorrect: false,
        message: `Not quite. The correct answer was: "${cQ.correctAnswer}".`,
        correctAnswer: cQ.correctAnswer
      });
    }
  }

  function handleFillInBlank(userInput) {
    const { checkType, requiredWord, question } = quiz[qIdx];
    let errors = [];
    let isCorrect = false;
    const isVerbQ = /verb/i.test(question);
    if (!checkRequiredWord(userInput, requiredWord, isVerbQ)) {
      errors.push(`You must use the word "${requiredWord}"${isVerbQ ? " (any correct form is OK)" : ""} in your sentence.`);
    }
    errors = errors.concat(checkSentenceStructure(userInput));
    isCorrect = errors.length === 0;
    setLastFeedback({
      userAnswer: userInput,
      isCorrect,
      errors,
      sample: makeSampleSentence(requiredWord, isVerbQ ? "verb" : checkType?.includes("adj") ? "adjective" : checkType?.includes("adv") ? "adverb" : "noun"),
    });
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 800);
    }
  }

  function handleIdentifyWords(userInput) {
    const cQ = quiz[qIdx];
    const typed = userInput.split(",").map(s => s.trim().toLowerCase());
    const correctArr = (cQ.correctAnswers || []).map(w => w.toLowerCase());
    const correctFound = typed.filter(w => correctArr.includes(w));
    const missed = correctArr.filter(w => !typed.includes(w));
    const extra = typed.filter(w => !correctArr.includes(w));
    const isCorrect = correctFound.length === correctArr.length && extra.length === 0;
    setLastFeedback({
      isCorrect,
      correctFound,
      missed,
      extra,
      correctAnswers: cQ.correctAnswers,
      message: isCorrect
        ? "Great job! You found all the correct words."
        : [
            missed.length > 0 ? `Missing: ${missed.join(", ")}` : null,
            extra.length > 0 ? `Extra (incorrect): ${extra.join(", ")}` : null,
          ].filter(Boolean).join(" | ")
    });
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 900);
    }
  }

  function handleMCQuizFinish() {
    if (qScore >= quiz.length / 2) {
      if (chapter < 5) {
        setChapter(ch => ch + 1);
        setPageIdx(0);
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(false);
  }

  /** =========== FINAL QUIZ =========== */
  function startFinalQuiz() {
    const final = generateFinalQuiz(grade, 60);
    setFinalQs(final);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(360);
    setShowFinal(true);
  }

  function isIdentifyCorrect(userInput, correctAnswers) {
    if (!userInput || !correctAnswers) return false;
    const typed = userInput.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
    const correctArr = correctAnswers.map(w => w.toLowerCase()).filter(Boolean);
    if (typed.length !== correctArr.length) return false;
    return typed.slice().sort().every((val, idx) => val === correctArr.slice().sort()[idx]);
  }

  function handleFinalAnswer(opt) {
    const cQ = finalQs[fIdx];
    if (cQ.questionType === "IDENTIFY") {
      if (isIdentifyCorrect(opt, cQ.correctAnswers)) setFScore(s => s + 1);
    } else if (cQ.questionType === "FITB") {
      const isVerbQ = /verb/i.test(cQ.question);
      const keywordOk = checkRequiredWord(opt, cQ.requiredWord, isVerbQ);
      const errors = checkSentenceStructure(opt);
      if (keywordOk && errors.length === 0) setFScore(s => s + 1);
    } else {
      if (opt === cQ.correctAnswer) setFScore(s => s + 1);
    }
    if (fIdx < finalQs.length - 1) setFIdx(i => i + 1);
    else setFFin(true);
  }

useEffect(() => {
  if (fFin && !hasAwardedTokens.current) {
    const percent = Math.round((fScore / finalQs.length) * 100);
    if (percent >= 80) {
      alert("You earned a token!");
      awardToken(); // <-- ACTUALLY AWARD THE TOKEN!
      if (typeof onAwardTokens === "function") {
        onAwardTokens(1);
      }
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["english_g5"] = {
      ...(quizResults["english_g5"] || {}),
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    hasAwardedTokens.current = true;
  }
}, [fFin, fScore, finalQs.length, onAwardTokens]);


  /** =========== RENDERs =========== */
  function ChapterSlide() {
    const currentPages = chapterPages[chapter];
    const page = currentPages[pageIdx];
    const isLastPage = pageIdx === currentPages.length - 1;
    return (
      <div>
        <h2>{page.title}</h2>
        <div>{page.body}</div>
        <div style={{ marginTop: 10 }}>
          {pageIdx > 0 && (
            <button className="button1" onClick={() => setPageIdx(p => p - 1)}>
              Back
            </button>
          )}
          {!isLastPage && (
            <button
              className="button1"
              onClick={() => setPageIdx(p => p + 1)}
              style={{ marginLeft: 10 }}
            >
              Next
            </button>
          )}
          {isLastPage && (
            <button
              className="button1"
              onClick={startChapterQuiz}
              style={{ marginLeft: 15 }}
            >
              Take Chapter {chapter} Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  function ChapterQuiz() {
    if (!quiz[qIdx]) return <p>Loading quiz...</p>;
    const cQ = quiz[qIdx];
    if (!quizFin) {
      switch (cQ.questionType) {
        case "MC":
          return (
            <StoryModeMultipleChoice
              cQ={cQ}
              onAnswerClick={handleAnswer}
              currentScore={qScore}
              quizLength={quiz.length}
              user={user}
              lastFeedback={lastFeedback}
              setLastFeedback={setLastFeedback}
            />
          );
        case "FITB":
          return (
            <StoryModeFillInBlank
              cQ={cQ}
              onSubmit={handleFillInBlank}
              currentScore={qScore}
              quizLength={quiz.length}
              user={user}
              lastFeedback={lastFeedback}
              setLastFeedback={setLastFeedback}
            />
          );
        case "IDENTIFY":
          return (
            <StoryModeIdentify
              cQ={cQ}
              onSubmit={handleIdentifyWords}
              currentScore={qScore}
              quizLength={quiz.length}
              user={user}
              lastFeedback={lastFeedback}
              setLastFeedback={setLastFeedback}
            />
          );
        default:
          return <p>Unknown question type</p>;
      }
    } else {
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={handleMCQuizFinish}>
            Continue
          </button>
        </div>
      );
    }
  }

  function ChapterUI() {
    if (!showQuiz) {
      return <ChapterSlide />;
    } else {
      return <ChapterQuiz />;
    }
  }

  return (
    <div className="grade3-english-page">
      {/* PROGRESS BAR */}
      <div style={{ margin: "10px 0" }}>
        <div
          style={{
            width: "80%",
            margin: "0 auto",
            background: "#ddd",
            borderRadius: 10,
            height: 20,
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              background: "#4caf50",
              height: 20,
              transition: "width 0.3s",
            }}
          />
        </div>
        <p>{progress}% complete</p>
      </div>

      {hasCompletedBefore && !showQuiz && !showFinal && (
        <div style={{ margin: "15px 0", textAlign: "center" }}>
          <p>
            You previously completed all Grade 5 English lessons. You can jump
            to any chapter or retake the final quiz.
          </p>
          <div>
            {[1,2,3,4,5].map(ch => (
              <button
                key={ch}
                className="button1"
                onClick={() => {
                  setChapDone(false);
                  setChapter(ch);
                  setPageIdx(0);
                  setShowQuiz(false);
                  setShowFinal(false);
                }}
              >
                Go to Chapter {ch}
              </button>
            ))}
            <button className="button1" onClick={startFinalQuiz}>
              Go to Final Quiz
            </button>
          </div>
        </div>
      )}

      {!chapDone && !showFinal && <ChapterUI />}

      {chapDone && !showFinal && (
        <div style={{ textAlign: "center" }}>
          <h3>All Chapters Completed!</h3>
          <button className="button1" onClick={startFinalQuiz}>
            Start Final Quiz
          </button>
        </div>
      )}

      {showFinal && (
        <FinalQuiz
          cQ={finalQs[fIdx]}
          fIdx={fIdx}
          finalQs={finalQs}
          handleFinalAnswer={handleFinalAnswer}
          fScore={fScore}
        />
      )}

      <br />
      <Link to="/english" className="button1">← Back to English Page</Link>
    </div>
  );
}
